import { gql } from "graphql-request";
import type { GraphQLClient } from "graphql-request";
import type { SystemAnomaliesData, AnomalyEntry } from "@nexus/widgets";
import type { DataResolver } from "@/lib/dashboard/resolverTypes";
import { getFilterValue, getTenantIds } from "./shared";

// ── System Anomalies ──────────────────────────────────────────────────────────

const SYSTEM_ANOMALIES_QUERY = gql`
  query SystemAnomalies(
    $from_ts: String!
    $to_ts: String!
    $tenant_ids: [Int!]
    $detection_domain: String
    $event_id: String
  ) {
    get_anomalies(
      from_ts: $from_ts
      to_ts: $to_ts
      tenant_ids: $tenant_ids
      detection_domain: $detection_domain
      event_id: $event_id
    ) {
      anomaly_id
      anomaly_type
      anomaly_severity
      anomaly_status
      anomaly_description
      raised_at
      resolved_at
      engine_id
      engine_label
      tenant_id
      tenant_label
      customer
      infringement_type
      detection_domain
      channel
      protected_event_id
      event_id
      event_label
      expected_value
      actual_value
      journal {
        timestamp
        analyst_name
        entry_type
        title
        content
      }
    }
  }
`;

type SystemAnomaliesResponse = {
  get_anomalies: Array<{
    anomaly_id: number;
    anomaly_type: string;
    anomaly_severity: string;
    anomaly_status: string;
    anomaly_description: string | null;
    raised_at: string;
    resolved_at: string | null;
    engine_id: number;
    engine_label: string;
    tenant_id: number;
    tenant_label: string;
    customer: string | null;
    infringement_type: string;
    detection_domain: string;
    channel: string;
    protected_event_id: number;
    event_id: number;
    event_label: string;
    expected_value: number;
    actual_value: number;
    journal: Array<{
      timestamp: string;
      analyst_name: string;
      entry_type: string;
      title: string;
      content: string;
    }>;
  }>;
};

function normalizeSystemAnomalies(raw: unknown): SystemAnomaliesData {
  const data = raw as SystemAnomaliesResponse;
  return {
    anomalies: (data.get_anomalies ?? []).map((a): AnomalyEntry => ({
      anomaly_id: Number(a.anomaly_id),
      anomaly_type: a.anomaly_type,
      anomaly_severity: a.anomaly_severity,
      anomaly_status: a.anomaly_status,
      anomaly_description: a.anomaly_description,
      raised_at: a.raised_at,
      resolved_at: a.resolved_at,
      engine_id: Number(a.engine_id),
      engine_label: a.engine_label,
      tenant_id: Number(a.tenant_id),
      tenant_label: a.tenant_label,
      customer: a.customer,
      infringement_type: a.infringement_type,
      detection_domain: a.detection_domain,
      channel: a.channel,
      protected_event_id: Number(a.protected_event_id),
      event_id: Number(a.event_id),
      event_label: a.event_label,
      expected_value: Number(a.expected_value),
      actual_value: Number(a.actual_value),
      journal: (a.journal ?? []).map((j) => ({
        timestamp: j.timestamp,
        analyst_name: j.analyst_name,
        entry_type: j.entry_type,
        title: j.title,
        content: j.content,
      })),
    })),
  };
}

// ── Resolvers ─────────────────────────────────────────────────────────────────

export const anomaliesDataSources: Record<string, DataResolver> = {
  "system-anomalies": {
    contract: { name: "system-anomalies", version: 1 },
    buildParams: (_requestParams, filters) => ({
      from_ts: filters.timeRange.from,
      to_ts: filters.timeRange.to,
      tenant_ids: getTenantIds(filters),
      detection_domain: getFilterValue(filters, "detection_domain"),
      event_id: getFilterValue(filters, "event_id"),
    }),
    fetcher: async (params, client: GraphQLClient) => {
      const raw = await client.request<SystemAnomaliesResponse>(SYSTEM_ANOMALIES_QUERY, params);
      return normalizeSystemAnomalies(raw);
    },
  },
};
